import { Box, Button, Icon, IconProps, Pill, Popover, Stack, Tab, Tabs, Tooltip } from '@sixfold/common-ui';
import { BackofficeScope } from '@sixfold/session-interface';
import { exhaustiveCheck, isNil, notNil } from '@sixfold/typed-primitives';
import classnames from 'classnames';
import React, { useCallback, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useLocation, useNavigate } from 'react-router';
import JsonView from 'react18-json-view';
import 'react18-json-view/src/style.css';

import { AnalyzeContextProvider } from './analyze_context';
import { ETALabel } from './eta_label';
import { ExternalEventList } from './external_event_list';
import { PlatformCallbackList } from './platform_callback_list';
import { TourTransportChainTab, TourRelatedChildToursTab } from './related_tours_tab';
import { useHighlightedStopIdContext } from './stop_highlight_context';
import { TourAnalyticsTab } from './tour_analytics_tab';
import { TourAnalyzeTab } from './tour_analyze_tab';
import { EventList } from './tour_event_list';
import { TourExternalEventLogList } from './tour_external_event_logs_list';
import { TourTripView } from './tour_trips';
import { TourVisibilityIssueList } from './tour_visibility_issue_list';
import { TourVisibilitySourceList } from './tour_visibility_source_list';
import { CompanyProfile } from '../../company/entities';
import { getTourKafkaQueryTimespan, KafkaContextProvider } from '../../components/bigquery/common/kafka_context';
import { TourKafkaTab } from '../../components/bigquery/tour_kafka/tour_kafka_tab';
import { TourTrafficTab } from '../../components/bigquery/tour_traffic/tour_traffic_tab';
import { ConfirmButton } from '../../components/confirm_button';
import { FormattedTimeSlot } from '../../components/date_formatting/date_formatted_timeslot';
import { FormattedDate } from '../../components/date_formatting/formatted_date';
import { useTimeZone } from '../../components/date_formatting/timezone_context';
import { TagsInput } from '../../components/tags_input';
import { useHasScopes, useIsAdmin } from '../../lib/authorization';
import { getEmbedConfig } from '../../lib/data';
import {
  EtaType,
  ExternalEtaSource,
  TourAllocationDeviceType,
  TourVehicleAllocationSource,
  TourVehicleAllocationType,
  TourVisibilitySourceType,
  Contact,
  TransportAlertSeverity,
} from '../../lib/graphql';
import { buildTrimbleTransportCloudLogsExplorerUrl } from '../../lib/util/google_console';
import { capitalizeFirstLetter, indexValueToPercentage } from '../../lib/util/string';
import {
  classicMapViewByAddressUrl,
  classicMapViewByCoordinatesUrl,
  placeViewUrl,
  shipmentViewUrl,
  tourStopEtaHistoryUrl,
} from '../../lib/util/url';
import { Routes } from '../../routes';
import {
  CustomField,
  EmissionsCalculationError,
  ExternalEvent,
  Tour,
  TourCO2Emissions,
  TourDrivenDistance,
  TourFuelBasedEmissions,
  TourStop,
  TourStopEta,
  TourStopLocation,
  TourVehicleAllocation,
} from '../entities';
import {
  createTpaButton,
  extractFieldValueFromCustomFields,
  getFormattedDelay,
  getFormattedGeocodingDirection,
  getFormattedGeocodingType,
  getFormattedLocation,
  getFormattedMatchLevel,
  getRowSpan,
  getMode,
  getStatusPillKind,
  isAirTour,
  isOceanTour,
  numberOfStatusProperties,
} from '../utils';
import { breakWordAtChar } from '../utils/break_word_util';

export interface TourDetailsViewDataProps {
  data: {
    tour: Tour;
    availableTourTags?: { name: string }[];
  };
}

export interface TourDetailsViewProps {
  className?: string;

  onTourAbort(tourId: number): void;

  onTourRecalculate(tourId: number): void;

  onAddTourTag(options: { tourId: string; tagName: string }): void;

  onRemoveTourTag(options: { tourId: string; tagName: string }): void;
}

export const TourDetailsView: React.FC<TourDetailsViewDataProps & TourDetailsViewProps> = ({
  className,
  data: { tour },
  onTourAbort,
  onTourRecalculate,
  onAddTourTag,
  onRemoveTourTag,
}) => {
  const config = getEmbedConfig();
  if (config === null) {
    throw Error('Missing config');
  }
  return (
    <div className={classnames('tour__details', className)}>
      <TourHeaderButtons
        tour={tour}
        onAddTourTag={onAddTourTag}
        onTourAbort={onTourAbort}
        onTourRecalculate={onTourRecalculate}
        onRemoveTourTag={onRemoveTourTag}
      />
      <TourDetailsMenu tour={tour} />
    </div>
  );
};

const DeliveryContactsRow: React.FunctionComponent<{
  shipperContacts: (Contact | null)[] | null;
  consigneeContacts: (Contact | null)[] | null;
  customerContacts: (Contact | null)[] | null;
}> = ({ shipperContacts, consigneeContacts, customerContacts }) => {
  const [showContacts, setShowContacts] = useState<boolean>(false);

  const nonNull = (c: Contact | null): c is Contact => c !== null;
  const shipper = (shipperContacts ?? []).filter(nonNull);
  const consignee = (consigneeContacts ?? []).filter(nonNull);
  const customer = (customerContacts ?? []).filter(nonNull);
  const totalCount = shipper.length + consignee.length + customer.length;

  const renderContactsGroup = (label: string, contacts: Contact[], keyPrefix: string) => {
    if (contacts.length === 0) {
      return null;
    }

    return (
      <>
        <tr>
          <td colSpan={3}>
            <span style={{ fontWeight: 700 }}>{label}</span>
          </td>
        </tr>
        <ContactsTableHeader />
        {contacts.map((contact, idx) => (
          <ContactRow contact={contact} key={`${keyPrefix}_${idx}`} />
        ))}
      </>
    );
  };

  if (totalCount === 0) {
    return (
      <tr>
        <td>
          <b>Contacts</b>
        </td>
        <td>No contacts</td>
      </tr>
    );
  }

  return (
    <>
      <tr>
        <td>
          <b>Contacts</b>
        </td>
        <td>
          <Button size="xsmall" onClick={() => setShowContacts(!showContacts)}>
            {showContacts ? 'Hide contacts' : `Show contacts (${totalCount})`}
          </Button>
        </td>
      </tr>
      {showContacts && (
        <tr>
          <td colSpan={2} style={{ paddingTop: 0 }}>
            <table className="ui-table compact-table" style={{ border: 'none' }}>
              <tbody>
                {renderContactsGroup('Shipper contacts', shipper, 'shipper')}
                {renderContactsGroup('Consignee contacts', consignee, 'consignee')}
                {renderContactsGroup('Customer contacts', customer, 'customer')}
              </tbody>
            </table>
          </td>
        </tr>
      )}
    </>
  );
};

const ContactsTableHeader: React.FunctionComponent = () => (
  <tr style={{ verticalAlign: 'top' }}>
    <td style={{ width: '25%' }}>Code</td>
    <td style={{ width: '37.5%' }}>Full name</td>
    <td style={{ width: '37.5%' }}>Email</td>
  </tr>
);

const ContactRow: React.FunctionComponent<{ contact: Contact }> = ({ contact }) => {
  const computedFullName = [contact.firstName ?? undefined, contact.lastName ?? undefined]
    .filter((v) => v !== undefined && v !== null && v !== '')
    .join(' ');
  const fullName = contact.fullName ?? computedFullName;

  return (
    <tr>
      <td>{contact.code ?? '-'}</td>
      <td>{fullName !== '' ? fullName : '-'}</td>
      <td>{contact.email ?? '-'}</td>
    </tr>
  );
};

/**
 *
 * Components for rendering parts of the tour details
 *
 */
const TourDetailsMenu: React.FunctionComponent<{ tour: Tour }> = ({ tour }) => {
  const isDeliveryEmpty = tour.deliveries.length === 0;
  const isVisibilityEmpty =
    tour.visibilitySegments.length === 0 &&
    tour.visibilitySources.length === 0 &&
    tour.visibilityIssues === null &&
    tour.trackingSegments.length === 0;
  const isEventsEmpty =
    tour.events.length === 0 &&
    tour.externalEvents.length === 0 &&
    tour.externalDeliveryEvents.length === 0 &&
    tour.externalStopEvents.length === 0;
  const isCarbonEmptyAndWithoutErrors = tour.plannedEmissions === null && tour.emissionsCalculationErrors?.length === 0;
  const hasNoTripData = isNil(tour.tripSegments) || tour.tripSegments.length === 0;

  const tourHasFull = tour.fullVehicleAllocationHistory.length !== 0;

  const effectiveIds = useMemo(
    () =>
      tourHasFull ? new Set(tour.vehicleAllocationHistory.map((a) => a.allocationId).filter((id) => id != null)) : null,
    [tourHasFull, tour.vehicleAllocationHistory],
  );

  const tourVehicleAllocations = useMemo<TourVehicleAllocation[]>(() => {
    const source = tourHasFull ? tour.fullVehicleAllocationHistory.slice() : tour.vehicleAllocationHistory.slice();
    const byAllocationIdDesc = (a: TourVehicleAllocation, b: TourVehicleAllocation) => {
      if (a.allocationId == null && b.allocationId == null) return 0;
      if (a.allocationId == null) return 1;
      if (b.allocationId == null) return -1;
      return Number(b.allocationId) - Number(a.allocationId);
    };
    if (effectiveIds === null) return source.sort(byAllocationIdDesc);
    // effective rows first, superseded rows below — each group sorted by allocationId desc
    return [
      ...source.filter((a) => a.allocationId == null || effectiveIds.has(a.allocationId)).sort(byAllocationIdDesc),
      ...source.filter((a) => a.allocationId != null && !effectiveIds.has(a.allocationId)).sort(byAllocationIdDesc),
    ];
  }, [tourHasFull, tour.fullVehicleAllocationHistory, tour.vehicleAllocationHistory, effectiveIds]);

  const allocationsById = useMemo(
    () => new Map(tourVehicleAllocations.filter((a) => a.allocationId != null).map((a) => [a.allocationId, a])),
    [tourVehicleAllocations],
  );

  const renderInfoTab = useCallback(
    () => (
      <Stack direction="column" gap="medium">
        <GeneralTourInfo tour={tour} />
        <TourAlerts tour={tour} />
        <TourForwardings tour={tour} allocationsById={allocationsById} />
        <TourVehicleAllocations
          allocations={tourVehicleAllocations}
          effectiveIds={effectiveIds}
          activeAllocation={tour.activeVehicleAllocation}
        />
        <PlatformCallbackList tour={tour} />
        <TourExternalEventLogList tour={tour} />
      </Stack>
    ),
    [tour, tourVehicleAllocations, effectiveIds, allocationsById],
  );

  const renderStopsTab = useCallback(() => <Stops tour={tour} />, [tour]);
  const renderKafkaTab = useCallback(() => <TourKafkaTab tour={tour} />, [tour]);
  const renderAPITrafficTab = useCallback(() => <TourTrafficTab tour={tour} />, [tour]);
  const renderAnalyzeTab = useCallback(() => <TourAnalyzeTab tour={tour} />, [tour]);
  const renderAnalyticsTab = useCallback(() => <TourAnalyticsTab tour={tour} />, [tour]);
  const renderTransportChainTab = useCallback(() => {
    if (tour.hasTransportChain) {
      return <TourTransportChainTab tour={tour} />;
    } else if (tour.relatedChildTours && tour.relatedChildTours.length > 0) {
      return <TourRelatedChildToursTab tour={tour} />;
    }
    return null;
  }, [tour]);

  const renderDeliveriesTab = useCallback(() => <Deliveries tour={tour} />, [tour]);

  const renderVisibilityTab = useCallback(
    () => (
      <Stack direction="column" gap="medium">
        <VisibilityRanges tour={tour} />
        <TrackingRanges tour={tour} />
        <TourVisibilitySourceList sources={tour.visibilitySources} />
        <TourVisibilityIssueList
          visibilityIssues={tour.visibilityIssues}
          mainVisibilityIssue={tour.mainVisibilityIssue}
        />
      </Stack>
    ),
    [tour],
  );

  const renderEventsTab = useCallback(
    () => (
      <Stack direction="column" gap="medium">
        <EventList events={tour.events} />
        <ExternalEventList
          tourId={tour.tour_id}
          events={([] as ExternalEvent[]).concat(
            tour.externalEvents,
            tour.externalDeliveryEvents,
            tour.externalStopEvents,
          )}
        />
      </Stack>
    ),
    [tour],
  );

  const renderUnifiedTab = useCallback(
    () => (
      <>
        <TourTripView
          segments={tour.tripSegments}
          locations={tour.tripLocations}
          deliveries={tour.tripDeliveryItems}
          oceanEtaAnalytics={tour.oceanEtaAnalytics}
        />
        {isOceanTour(tour.visibilityServices) && tour.oceanEtaAnalytics !== null ? (
          <div className="item tour__tour-details__stop-item">
            <table className="ui-table compact-table">
              <thead>
                <tr>
                  <th colSpan={3}>
                    <div className="tour__tour-details__stop-item__stop-header">
                      <h3 className="tour__tour-details__stop-item__stop-header__address">Ocean ETA analytics</h3>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={{ width: '18.75%' }}>
                    <b>Compared at</b>
                  </td>
                  <td colSpan={2}>
                    <FormattedDate date={tour.oceanEtaAnalytics.comparedAt} />
                  </td>
                </tr>
                <tr>
                  <td>
                    <b>ML arrival time</b>
                  </td>
                  <td colSpan={2}>
                    {tour.oceanEtaAnalytics.arrivalTime ? (
                      <FormattedDate date={tour.oceanEtaAnalytics.arrivalTime} />
                    ) : (
                      '-'
                    )}
                  </td>
                </tr>
                <tr>
                  <td>
                    <b>ML milestone id</b>
                  </td>
                  <td colSpan={2}>{tour.oceanEtaAnalytics.milestoneId ?? '-'}</td>
                </tr>
                <tr>
                  <td>
                    <b>ML qualifier</b>
                  </td>
                  <td colSpan={2}>{tour.oceanEtaAnalytics.qualifier ?? '-'}</td>
                </tr>
                <tr>
                  <td>
                    <b>Carrier ETA</b>
                  </td>
                  <td colSpan={2}>
                    {tour.oceanEtaAnalytics.carrierEta ? (
                      <FormattedDate date={tour.oceanEtaAnalytics.carrierEta} />
                    ) : (
                      '-'
                    )}
                  </td>
                </tr>
                <tr>
                  <td>
                    <b>Carrier ETA source</b>
                  </td>
                  <td colSpan={2}>{tour.oceanEtaAnalytics.carrierEtaSource ?? '-'}</td>
                </tr>
                <tr>
                  <td>
                    <b>Time difference (min)</b>
                  </td>
                  <td colSpan={2}>{tour.oceanEtaAnalytics.timeDifferenceMinutes ?? '-'}</td>
                </tr>
                <tr>
                  <td>
                    <b>Accuracy window</b>
                  </td>
                  <td colSpan={2}>{tour.oceanEtaAnalytics.accuracyWindow ?? '-'}</td>
                </tr>
                <tr>
                  <td>
                    <b>Model version</b>
                  </td>
                  <td colSpan={2}>{tour.oceanEtaAnalytics.modelVersion ?? '-'}</td>
                </tr>
                <tr>
                  <td>
                    <b>Processing duration (ms)</b>
                  </td>
                  <td colSpan={2}>{tour.oceanEtaAnalytics.processingDurationMs ?? '-'}</td>
                </tr>
                <tr>
                  <td>
                    <b>Model variant</b>
                  </td>
                  <td colSpan={2}>{tour.oceanEtaAnalytics.modelVariant ?? '-'}</td>
                </tr>
              </tbody>
            </table>
          </div>
        ) : null}
      </>
    ),
    [tour],
  );

  const renderCarbonTab = useCallback(
    () => (
      <>
        <CalculatedEmissions type="planned" emissions={tour.plannedEmissions} />
        <FuelBasedEmissions emissions={tour.fuelBasedEmissions} />
        <DrivenDistance data={tour.drivenDistance} />
        <EmissionsCalculationErrors calculationErrors={tour.emissionsCalculationErrors}></EmissionsCalculationErrors>
      </>
    ),
    [tour],
  );

  const navigate = useNavigate();
  const location = useLocation();
  const tourId = tour.tour_id.toString();
  const showRelated = tour.hasTransportChain || (tour.relatedChildTours && tour.relatedChildTours.length > 0);

  const tabDefs = [
    { id: 'info', title: 'Info', path: Routes.Tour.generatePath({ tour_id: tourId }), render: renderInfoTab },
    { id: 'stops', title: 'Stops', path: Routes.TourStops.generatePath({ tour_id: tourId }), render: renderStopsTab },
    {
      id: 'deliveries',
      title: 'Deliveries',
      path: Routes.TourDeliveries.generatePath({ tour_id: tourId }),
      disabled: isDeliveryEmpty,
      render: renderDeliveriesTab,
    },
    {
      id: 'visibility',
      title: 'Visibility',
      path: Routes.TourVisibility.generatePath({ tour_id: tourId }),
      disabled: isVisibilityEmpty,
      render: renderVisibilityTab,
    },
    {
      id: 'events',
      title: 'Events',
      path: Routes.TourEvents.generatePath({ tour_id: tourId }),
      disabled: isEventsEmpty,
      render: renderEventsTab,
    },
    {
      id: 'carbon',
      title: 'CO₂',
      path: Routes.TourCarbon.generatePath({ tour_id: tourId }),
      disabled: isCarbonEmptyAndWithoutErrors,
      render: renderCarbonTab,
    },
    {
      id: 'unified',
      title: 'Unified',
      path: Routes.TourUnified.generatePath({ tour_id: tourId }),
      disabled: hasNoTripData,
      render: renderUnifiedTab,
    },
    ...(showRelated
      ? [
          {
            id: 'related',
            title: 'Related',
            path: Routes.TourRelatedTransports.generatePath({ tour_id: tourId }),
            render: renderTransportChainTab,
          },
        ]
      : []),
    {
      id: 'analytics',
      title: 'Analytics',
      path: Routes.TourAnalytics.generatePath({ tour_id: tourId }),
      render: renderAnalyticsTab,
    },
    { id: 'kafka', title: 'Kafka', path: Routes.TourKafka.generatePath({ tour_id: tourId }), render: renderKafkaTab },
    {
      id: 'traffic',
      title: 'API Traffic',
      path: Routes.TourTraffic.generatePath({ tour_id: tourId }),
      render: renderAPITrafficTab,
    },
    {
      id: 'analyze',
      title: 'Analyze',
      path: Routes.TourAnalyze.generatePath({ tour_id: tourId }),
      render: renderAnalyzeTab,
    },
  ];

  const activeIndex = Math.max(
    0,
    tabDefs.findIndex((t) => location.pathname === t.path),
  );

  const { initialFromDate, initialToDate } = getTourKafkaQueryTimespan(tour);
  return (
    <React.Fragment>
      <Helmet title={`Tour #${tour.tour_id}`} />
      <KafkaContextProvider initialFromDate={initialFromDate} initialToDate={initialToDate}>
        <AnalyzeContextProvider>
          <Tabs
            className="tour__details__tabs"
            activeIndex={activeIndex}
            onTabChanged={(index) => {
              const tab = tabDefs[index];
              if (tab !== undefined) {
                void navigate({ pathname: tab.path, search: window.location.search });
              }
            }}>
            {tabDefs.map((tab) => (
              <Tab key={tab.id} id={tab.id} title={tab.title} disabled={tab.disabled} padding={0}>
                <Box paddingBlockStart="medium" paddingInline="small">
                  {tab.render()}
                </Box>
              </Tab>
            ))}
          </Tabs>
        </AnalyzeContextProvider>
      </KafkaContextProvider>
    </React.Fragment>
  );
};

const TRIMBLE_TRANSPORTATION_CLOUD_BRANCH_ID = 'TRIMBLE_TRANSPORTATION_CLOUD';

const TourAssociationEvaluationButtonRow: React.FunctionComponent<{
  showsJson: boolean;
  setShowsJson: (value: boolean) => void;
}> = ({ showsJson, setShowsJson }) => (
  <tr>
    <td>Tour association</td>
    <td>
      <Button size="small" onClick={() => setShowsJson(!showsJson)}>
        {showsJson ? 'Hide metadata' : 'Show metadata'}
      </Button>
    </td>
  </tr>
);

const TourAssociationEvaluationJsonRow: React.FunctionComponent<{ associationEvaluation: string }> = ({
  associationEvaluation,
}) => {
  let parsed: unknown = associationEvaluation;
  try {
    parsed = JSON.parse(associationEvaluation);
  } catch {
    // Fall back to raw string when not valid JSON.
  }

  return (
    <tr>
      <td colSpan={3}>
        <div
          style={{
            fontFamily: 'monospace',
            backgroundColor: 'var(--color-neutral-bg-surface-sunken)',
            padding: '8px',
          }}>
          <JsonView src={parsed} enableClipboard={true} />
        </div>
      </td>
    </tr>
  );
};

const GeneralTourInfo: React.FunctionComponent<{ tour: Tour }> = ({ tour }) => {
  const mode = getMode(tour.visibilityServices);
  const navigate = useNavigate();
  const isMobileTour =
    tour.activeVehicleAllocation?.allocationDeviceType === TourAllocationDeviceType.MOBILE ||
    tour.visibilitySources.some(({ primaryEventSource }) =>
      [TourVisibilitySourceType.MOBILE, TourVisibilitySourceType.TTC_MOBILE].includes(primaryEventSource),
    );
  const [showsAssociationJson, setShowsAssociationJson] = useState<boolean>(false);

  const tpaButton = createTpaButton(tour, 'tpaProductionUrl', 'TPA UI');
  const tpaTestButton = createTpaButton(tour, 'tpaTestUrl', 'TPA UI - AC');
  const tpaIntButton = createTpaButton(tour, 'tpaIntUrl', 'TPA UI - IN');
  const tpaDevButton = createTpaButton(tour, 'tpaDevUrl', 'TPA UI - DV');

  const showTrimbleLogsExplorerRow = tour.platformBranchId === TRIMBLE_TRANSPORTATION_CLOUD_BRANCH_ID;
  const platformSectionRowSpan = showTrimbleLogsExplorerRow ? 6 : 5;

  return (
    <table className="ui-table compact-table">
      <tbody>
        <tr style={{ verticalAlign: 'top' }}>
          <td style={{ width: '18.75%' }} rowSpan={17}>
            <b>General</b>
          </td>
          <td style={{ width: '31.25%' }}>Customer UI</td>
          <td>
            {tour.shipper !== null ? (
              <a
                target="_blank"
                title="Shipper"
                href={shipmentViewUrl(tour.shipper?.company_id ?? null, tour.tour_id.toString(), mode)}
                rel="noreferrer">
                📦
              </a>
            ) : (
              <span>No shipper to link to</span>
            )}
            &nbsp;
            {tour.carrier !== null ? (
              <a
                target="_blank"
                title="Carrier"
                href={shipmentViewUrl(tour.carrier?.company_id ?? null, tour.tour_id.toString(), mode)}
                rel="noreferrer">
                🚛
              </a>
            ) : (
              <span>No carrier to link to</span>
            )}
            &nbsp;
            {tpaButton}
            {tpaTestButton}
            {tpaIntButton}
            {tpaDevButton}
            {isMobileTour && (
              <Button
                kind="neutral"
                size="small"
                iconStart="small-mobile"
                onClick={() => navigate(Routes.MobileTour.generatePath({ tour_id: tour.tour_id.toString() }))}>
                Mobile Command Center
              </Button>
            )}
          </td>
        </tr>
        <tr>
          <td>Transport ID</td>
          <td>{tour.transport_id}</td>
        </tr>
        <tr>
          <td>Shipper transport number</td>
          <td>{tour.shipper_transport_number ?? '-'}</td>
        </tr>
        <tr>
          <td>Site</td>
          <td>
            {tour.site !== null ? (
              <>
                {tour.site.name} ({tour.site.site_id})
              </>
            ) : (
              '-'
            )}
          </td>
        </tr>
        <tr>
          <td>User groups</td>
          <td>
            {tour.userGroupsOnPlatform.map((userGroupOnPlatform, idx) => (
              <React.Fragment key={userGroupOnPlatform.groupId}>
                <Link
                  style={{ wordBreak: 'break-word' }}
                  to={Routes.CompanyUserGroup.generatePath({
                    company_id: userGroupOnPlatform.company?.company_id ?? tour.shipper?.company_id ?? 'NA',
                    group_id: userGroupOnPlatform.groupId,
                  })}>
                  {userGroupOnPlatform.name}
                </Link>
                {idx < tour.userGroupsOnPlatform.length - 1 && <>, </>}
              </React.Fragment>
            ))}
          </td>
        </tr>
        <tr>
          <td>Visibility index</td>
          <td>
            <div style={visibilityIndexStyle(tour.visibilityIndex)}>{indexValueToPercentage(tour.visibilityIndex)}</div>
          </td>
        </tr>
        <tr>
          <td>Visibility services</td>
          <td>{tour.visibilityServices.length > 0 ? tour.visibilityServices.join(', ') : '-'}</td>
        </tr>
        <tr>
          <td>Bill of lading number</td>
          <td>{tour.bill_of_lading ?? extractFieldValueFromCustomFields(tour, 'billofladingNumber')}</td>
        </tr>
        <tr>
          <td>Equipment number</td>
          <td>{extractFieldValueFromCustomFields(tour, 'equipmentNumber')}</td>
        </tr>
        <tr>
          <td>Booking number</td>
          <td>{extractFieldValueFromCustomFields(tour, 'bookingNumber')}</td>
        </tr>
        <tr>
          <td>SCAC</td>
          <td>{extractFieldValueFromCustomFields(tour, 'scac')}</td>
        </tr>
        <tr>
          <td>Tour type</td>
          <td>{tour.tour_type ?? '-'}</td>
        </tr>
        <tr>
          <td>Parent tour id</td>
          <td>
            {tour.parent_tour_id !== null ? (
              <Link to={Routes.Tour.generatePath({ tour_id: tour.parent_tour_id })}>{tour.parent_tour_id}</Link>
            ) : (
              '-'
            )}
          </td>
        </tr>
        <tr>
          <td>Volume</td>
          <td>
            {tour.volume?.value ?? '-'} {tour.volume?.unit}
          </td>
        </tr>
        <tr>
          <td>External carrier</td>
          <td>
            Name: {tour.externalCarrier?.companyName ?? '-'} (ID: {tour.externalCarrier?.companyID ?? '-'})
          </td>
        </tr>
        <tr>
          <td>Load type</td>
          <td>{tour.loadType ?? '-'}</td>
        </tr>
        <tr>
          <td>Shipment load type</td>
          <td>{tour.shipmentType ?? '-'}</td>
        </tr>
        <tr>
          <td>Incoterms</td>
          <td>{tour.incoterm ? `${tour.incoterm.term ?? '-'} / ${tour.incoterm.city ?? '-'}` : '-'}</td>
        </tr>
        <tr>
          <td>Main mode</td>
          <td>{tour.mainMode ?? '-'}</td>
        </tr>
        <tr style={{ verticalAlign: 'top' }}>
          <td rowSpan={3}>
            <b>Participants</b>
          </td>
          <td>Carrier</td>
          <td>
            {tour.carrier !== null ? (
              <Link to={Routes.Company.generatePath({ company_id: tour.carrier.company_id })}>
                {tour.carrier.company_name}
              </Link>
            ) : (
              '-'
            )}
          </td>
        </tr>
        <tr>
          <td>Shipper</td>
          <td>
            {tour.shipper !== null ? (
              <Link to={Routes.Company.generatePath({ company_id: tour.shipper.company_id })}>
                {tour.shipper.company_name}
              </Link>
            ) : (
              '-'
            )}
          </td>
        </tr>
        <tr>
          <td>Created by company</td>
          <td>
            {tour.createdBy !== null ? (
              <Link to={Routes.Company.generatePath({ company_id: tour.createdBy.company_id })}>
                {tour.createdBy.company_name}
              </Link>
            ) : (
              '-'
            )}
          </td>
        </tr>
        <tr style={{ verticalAlign: 'top' }}>
          <td rowSpan={getRowSpan(tour, numberOfStatusProperties(tour), null, 'abort_time', 'aborted_by_user')}>
            <b>Status calculation</b>
          </td>
          <td>Status</td>
          <td>
            <Pill kind={getStatusPillKind(tour.status)}>{tour.status ?? '-'}</Pill>
          </td>
        </tr>
        <tr>
          <td>Vehicle</td>
          <td>
            <VehicleInfo vehicle={tour.currentVehicle} />
          </td>
        </tr>
        <tr>
          <td>Directions provider</td>
          <td>{tour.route?.provider ?? '-'}</td>
        </tr>
        <tr>
          <td>Transport mode</td>
          <td>{tour.transportMode ?? '-'}</td>
        </tr>
        <tr>
          <td>Tour total distance</td>
          <td>
            {tour.tourDistance?.totalDistanceInMeters !== undefined
              ? `${tour.tourDistance.totalDistanceInMeters}m`
              : '-'}
          </td>
        </tr>
        <tr>
          <td>Created at</td>
          <td>
            <FormattedDate date={tour.created_at} />
          </td>
        </tr>
        <tr>
          <td>Start time</td>
          <td>
            <FormattedDate date={tour.start_time} />
          </td>
        </tr>
        <tr>
          <td>Complete time</td>
          <td>
            <FormattedDate date={tour.complete_time} />
          </td>
        </tr>
        <tr>
          <td>Visibility start time</td>
          <td>
            <FormattedDate date={tour.visibility_start_time} />
          </td>
        </tr>
        {tour.activeVehicleAllocation !== null &&
          tour.activeVehicleAllocation.allocationType === TourVehicleAllocationType.TOUR_ALLOCATION &&
          tour.activeVehicleAllocation.startTrackingAt !== null && (
            <tr>
              <td>External tracking start</td>
              <td>
                <FormattedDate date={tour.activeVehicleAllocation.startTrackingAt} />
              </td>
            </tr>
          )}
        <tr>
          <td>Visibility end time</td>
          <td>
            <FormattedDate date={tour.visibility_end_time} />
          </td>
        </tr>
        <tr>
          <td>Tracking start time</td>
          <td>
            <FormattedDate date={tour.trackingStartTime} />
          </td>
        </tr>
        <tr>
          <td>Tracking end time</td>
          <td>
            <FormattedDate date={tour.trackingEndTime} />
          </td>
        </tr>
        <tr>
          <td>Abandon time</td>
          <td>
            <FormattedDate date={tour.metadata?.abandon_time} />
          </td>
        </tr>
        <tr>
          <td>Warnings</td>
          <td>
            {tour.warnings.length > 0
              ? tour.warnings.map(({ type, metadata }, index) => {
                  return (
                    <React.Fragment key={index}>
                      {index !== 0 ? ', ' : ''}
                      {type}
                      {metadata && Object.keys(metadata).length > 0 ? (
                        <Tooltip content={<div>{JSON.stringify(metadata, null, 2)}</div>}>
                          <i className="info circle icon" />
                        </Tooltip>
                      ) : (
                        ''
                      )}
                    </React.Fragment>
                  );
                })
              : '-'}
          </td>
        </tr>
        {tour.isTrackingDisabled === true && (
          <tr>
            <td>Tracking</td>
            <td>
              <div className={classnames('ui label', 'red')}>{'disabled'}</div>
            </td>
          </tr>
        )}
        <tr>
          <td>Failure reason</td>
          <td>{tour.failureReason?.type ?? '-'}</td>
        </tr>
        {tour.abort_time !== null && (
          <tr>
            <td>Abort time</td>
            <td>
              <FormattedDate date={tour.abort_time} />
            </td>
          </tr>
        )}
        {tour.aborted_by_user !== null && (
          <tr>
            <td>Aborted by</td>
            <td>
              <Link to={Routes.User.generatePath({ user_id: tour.aborted_by_user.user_id })}>
                {tour.aborted_by_user.first_name} {tour.aborted_by_user.last_name}
              </Link>
            </td>
          </tr>
        )}
        {tour.metadata?.association_evaluation != null && (
          <TourAssociationEvaluationButtonRow showsJson={showsAssociationJson} setShowsJson={setShowsAssociationJson} />
        )}
        {tour.metadata?.association_evaluation != null && showsAssociationJson && (
          <TourAssociationEvaluationJsonRow associationEvaluation={tour.metadata.association_evaluation} />
        )}
        <tr style={{ verticalAlign: 'top' }}>
          <td rowSpan={platformSectionRowSpan}>
            <b>Platform</b>
          </td>
          <td>Name</td>
          <td>{tour.platform?.platform_name ?? '-'}</td>
        </tr>
        <tr>
          <td>Platform branch ID</td>
          <td>{tour.platformBranchId ?? '-'}</td>
        </tr>
        <tr>
          <td>Platform transport number</td>
          <td>{tour.platform_transport_number ?? '-'}</td>
        </tr>
        <tr>
          <td>Platform carrier ID</td>
          <td>{tour.platform_carrier_id ?? '-'}</td>
        </tr>
        <tr>
          <td>Platform shipper ID</td>
          <td>{tour.platform_shipper_id ?? '-'}</td>
        </tr>
        {showTrimbleLogsExplorerRow && (
          <tr>
            <td>&nbsp;</td>
            <td>
              <a
                className="link"
                rel="noopener noreferrer"
                target="_blank"
                href={buildTrimbleTransportCloudLogsExplorerUrl(tour)}>
                Open in Logs Explorer
              </a>
            </td>
          </tr>
        )}
        {tour.customFields.length > 0 && (
          <>
            <tr style={{ verticalAlign: 'top' }}>
              <td rowSpan={5}>
                <b>Custom fields</b>
              </td>
              <td>Name</td>
              <td>Value(s)</td>
              <td>Visibility</td>
            </tr>
            <CustomFieldsTableItems customFields={tour.customFields} />
          </>
        )}
      </tbody>
    </table>
  );
};

const Stops: React.FunctionComponent<{ tour: Pick<Tour, 'working_stop_id' | 'stops' | 'tour_id' | 'createdBy'> }> = ({
  tour: { working_stop_id, stops, tour_id, createdBy },
}) => {
  const [collapsedStopIds, setCollapsedStopIds] = useState(new Set());
  const { setHighlightedStopId } = useHighlightedStopIdContext();
  const allStopsCollapsed = stops.length > 0 && stops.every(({ stop_id }) => collapsedStopIds.has(stop_id));

  const toggleAllStopCollapsing = useCallback(() => {
    if (allStopsCollapsed) {
      setCollapsedStopIds(new Set());
    } else {
      setCollapsedStopIds(new Set(stops.map(({ stop_id }) => stop_id)));
    }
  }, [allStopsCollapsed, setCollapsedStopIds, stops]);

  const toggleStopCollapsing = useCallback(
    (stopId: number) => {
      const newSet = new Set(collapsedStopIds);

      if (collapsedStopIds.has(stopId)) {
        newSet.delete(stopId);
      } else {
        newSet.add(stopId);
      }

      setCollapsedStopIds(newSet);
    },
    [setCollapsedStopIds, collapsedStopIds],
  );

  const { timeZone } = useTimeZone();

  return (
    <div>
      <h3>
        {`Stops (${stops.length})`}
        <Button
          kind="ghost"
          size="xsmall"
          iconStart={allStopsCollapsed ? 'small-chevron-down' : 'small-chevron-up'}
          hideLabel
          onClick={toggleAllStopCollapsing}
          style={{ float: 'right' }}>
          Toggle all stops
        </Button>
      </h3>
      {stops.map((stop) => (
        <div
          className="item tour__tour-details__stop-item"
          key={stop.stop_id}
          onMouseEnter={() => setHighlightedStopId(stop.stop_id)}
          onMouseLeave={() => setHighlightedStopId(undefined)}>
          <table className="ui-table compact-table">
            <thead>
              <tr>
                <th colSpan={3}>
                  <div className="tour__tour-details__stop-item__stop-header">
                    <h3 className="tour__tour-details__stop-item__stop-header__address">
                      <i
                        className={classnames({
                          'map pin icon': true,
                          stop_loading: stop.type === 'loading',
                          stop_unloading: stop.type === 'unloading',
                        })}
                      />
                      {stop.display_sequence_number}. {getFormattedLocation(stop.location)}
                    </h3>
                    {stop.status !== null && (stop.status === 'at-stop' || stop.status === 'departed') ? (
                      <Pill kind="success">{capitalizeFirstLetter(stop.status)}</Pill>
                    ) : (
                      <ETALabel
                        eta={
                          stop.estimated_arrival !== null
                            ? timeZone === 'UTC'
                              ? stop.estimated_arrival
                              : new Date(stop.estimated_arrival)
                            : null
                        }
                        status={stop.estimated_arrival_status ?? null}
                      />
                    )}
                    {stop.stop_id === working_stop_id ? <Pill kind="neutral">Working stop</Pill> : null}
                    <Button
                      kind="ghost"
                      size="xsmall"
                      iconStart={collapsedStopIds.has(stop.stop_id) ? 'small-chevron-down' : 'small-chevron-up'}
                      hideLabel
                      onClick={() => toggleStopCollapsing(stop.stop_id)}>
                      Toggle stop
                    </Button>
                  </div>
                </th>
              </tr>
            </thead>
            {!collapsedStopIds.has(stop.stop_id) && (
              <tbody>
                <tr style={{ verticalAlign: 'top' }}>
                  <td style={{ width: '18.75%' }} rowSpan={getRowSpan(stop, 3, null, 'type')}>
                    <b>General</b>
                  </td>
                  <td style={{ width: '31.25%' }}>Stop ID</td>
                  <td>{stop.stop_id ?? '-'}</td>
                </tr>
                {stop.type != null && (
                  <tr>
                    <td>Type</td>
                    <td>{stop.type}</td>
                  </tr>
                )}
                {stop.external_type != null && (
                  <tr>
                    <td>External type</td>
                    <td>{stop.external_type}</td>
                  </tr>
                )}
                <tr>
                  <td>Status</td>
                  <td>{stop.status ?? '-'}</td>
                </tr>

                <tr style={{ verticalAlign: 'top' }}>
                  <td rowSpan={getRowSpan(stop, 3, null, 'booking_id')}>
                    <b>IDs</b>
                  </td>
                  <td>Reference</td>
                  <td>{stop.stop_reference_id ?? '-'}</td>
                </tr>
                {stop.booking_id != null && (
                  <tr>
                    <td>Booking</td>
                    <td>{stop.booking_id ?? '-'}</td>
                  </tr>
                )}
                <tr>
                  <td>Deliveries</td>
                  <td>
                    {stop.delivery_ids !== null && stop.delivery_ids.length > 0 ? stop.delivery_ids.join(', ') : '-'}
                  </td>
                </tr>
                <tr style={{ verticalAlign: 'top' }}>
                  <td
                    rowSpan={
                      (stop.actual_sequence_number !== stop.sequence_number ? 1 : 0) +
                      (stop.sequence_number !== stop.platformSequenceNumber ? 1 : 0) +
                      1
                    }>
                    <b>Sequence number</b>
                  </td>
                  <td>
                    <Tooltip content={<>Stop sequence sent from platform</>}>
                      <div className="hoverable">Platform</div>
                    </Tooltip>
                  </td>
                  <td>{stop.platformSequenceNumber ?? '-'}</td>
                </tr>
                {stop.actual_sequence_number !== stop.sequence_number && (
                  <tr>
                    <td>
                      <Tooltip content={<>Real stop visit sequence for truck</>}>
                        <div className="hoverable">Actual</div>
                      </Tooltip>
                    </td>
                    <td>{stop.actual_sequence_number ?? '-'}</td>
                  </tr>
                )}
                {stop.sequence_number !== stop.platformSequenceNumber && (
                  <tr>
                    <td>
                      <Tooltip content={<>Sequence calculated using platform and delivery sequence</>}>
                        <div className="hoverable">Calculated</div>
                      </Tooltip>
                    </td>
                    <td>{stop.sequence_number}</td>
                  </tr>
                )}

                <tr style={{ verticalAlign: 'top' }}>
                  <td
                    rowSpan={getRowSpan(
                      stop.location,
                      3,
                      (obj: TourStopLocation, key: keyof TourStopLocation) => {
                        return key === 'address' && obj.address?.full_address == null;
                      },
                      'address',
                    )}>
                    <b>Location</b>
                  </td>
                  <td>Map links</td>
                  <td>
                    <a
                      className="link"
                      rel="noopener noreferrer"
                      target="_blank"
                      title="Map view"
                      href={classicMapViewByAddressUrl({
                        address: getFormattedLocation(stop.location),
                      })}>
                      🗺️
                    </a>{' '}
                    <a
                      className="link"
                      rel="noopener noreferrer"
                      target="_blank"
                      title="Satellite view"
                      href={classicMapViewByCoordinatesUrl({
                        lat: stop.location?.position?.lat ?? null,
                        lng: stop.location?.position?.lng ?? null,
                      })}>
                      🛰
                    </a>
                  </td>
                </tr>
                <tr>
                  <td>
                    Address
                    <Tooltip
                      content={<>The displayed address might not be the one provided by the transport creator.</>}>
                      <span>
                        <Icon name="small-info" />
                      </span>
                    </Tooltip>
                  </td>
                  <td>{stop.location?.address?.full_address ?? '-'}</td>
                </tr>
                <tr>
                  <td>Place reference ID</td>
                  <td>{stop.place_reference_id ?? '-'}</td>
                </tr>
                <LocationDetails stop={stop} tourCreatedBy={createdBy} />
                <tr style={{ verticalAlign: 'top' }}>
                  <td rowSpan={notNil(stop.vehicleAllocation?.startTrackingAt) ? 2 : 1}>
                    <b>Allocation</b>
                  </td>
                  <td>Vehicle</td>
                  <td>
                    <VehicleInfo vehicle={stop.vehicleAllocation} />
                  </td>
                </tr>
                {notNil(stop.vehicleAllocation?.startTrackingAt) && (
                  <tr>
                    <td>External tracking start</td>
                    <td>
                      <FormattedDate date={stop.vehicleAllocation?.startTrackingAt} />
                    </td>
                  </tr>
                )}

                <tr style={{ verticalAlign: 'top' }}>
                  <td
                    rowSpan={getRowSpan(
                      stop,
                      9,
                      null,
                      'expected_duration',
                      'deadline',
                      'arrival_time',
                      'arrival_delay_in_seconds',
                      'departure_time',
                      'originalTimeslot',
                      'revisedTimeslot',
                    )}>
                    <b>Timeslot / Timing</b>
                  </td>
                  <td>Timeslot</td>
                  <td>
                    {stop.timeslot !== null ? (
                      <FormattedTimeSlot startDate={stop.timeslot.begin} endDate={stop.timeslot.end} />
                    ) : (
                      '-'
                    )}
                  </td>
                </tr>
                <tr>
                  <td>Timeslot in stop timezone</td>
                  <td>
                    {stop.timeslot !== null && stop.timeslot.timezone !== null ? (
                      <FormattedTimeSlot
                        startDate={stop.timeslot.begin}
                        endDate={stop.timeslot.end}
                        timezone={stop.timeslot.timezone}
                      />
                    ) : (
                      '-'
                    )}
                  </td>
                </tr>
                {stop.originalTimeslot !== null && (
                  <tr>
                    <td>Original timeslot</td>
                    <td>
                      <FormattedTimeSlot startDate={stop.originalTimeslot.begin} endDate={stop.originalTimeslot.end} />
                    </td>
                  </tr>
                )}
                {stop.revisedTimeslot !== null && (
                  <tr>
                    <td>Revised timeslot</td>
                    <td>
                      <FormattedTimeSlot startDate={stop.revisedTimeslot.begin} endDate={stop.revisedTimeslot.end} />
                    </td>
                  </tr>
                )}
                {stop.expected_duration != null && (
                  <tr>
                    <td>Expected duration</td>
                    <td>{stop.expected_duration}</td>
                  </tr>
                )}
                {stop.deadline !== null && (
                  <tr>
                    <td>Deadline</td>
                    <td>
                      <FormattedDate date={stop.deadline} />
                    </td>
                  </tr>
                )}
                {stop.arrival_time !== null && (
                  <tr>
                    <td>Arrival time</td>
                    <td>
                      <FormattedDate date={stop.arrival_time} />
                    </td>
                  </tr>
                )}
                {stop.arrival_delay_in_seconds !== null && (
                  <tr>
                    <td>Arrival delay</td>
                    <td>{getFormattedDelay(stop.arrival_delay_in_seconds)}</td>
                  </tr>
                )}
                {stop.departure_time !== null && (
                  <tr>
                    <td>Departure time</td>
                    <td>
                      <FormattedDate date={stop.departure_time} />
                    </td>
                  </tr>
                )}
              </tbody>
            )}
          </table>
          {!collapsedStopIds.has(stop.stop_id) && <StopETADetails stop={stop} tourId={tour_id} />}
        </div>
      ))}
    </div>
  );
};

const LocationDetails: React.FunctionComponent<{
  stop: TourStop;
  tourCreatedBy: CompanyProfile | null;
}> = ({ stop, tourCreatedBy }) => {
  const [showAddLocInfo, setShowAddLocInfo] = useState<boolean>(false);
  return (
    <React.Fragment>
      {showAddLocInfo && (
        <React.Fragment>
          <tr style={{ verticalAlign: 'top' }}>
            <td style={{ width: '18.75%' }} rowSpan={15}>
              <b>{'Additional location info'}</b>
            </td>
            <td rowSpan={6}>Customer-provided address</td>
            <td>country: {stop.location?.customerProvidedAddress?.country ?? '-'}</td>
          </tr>
          <tr>
            <td>city: {stop.location?.customerProvidedAddress?.city ?? '-'}</td>
          </tr>
          <tr>
            <td>zipcode: {stop.location?.customerProvidedAddress?.zipcode ?? '-'}</td>
          </tr>
          <tr>
            <td>street_address: {stop.location?.customerProvidedAddress?.street_address ?? '-'}</td>
          </tr>
          <tr>
            <td>state: {stop.location?.customerProvidedAddress?.state ?? '-'}</td>
          </tr>
          <tr>
            <td>company_name: {stop.location?.name ?? '-'}</td>
          </tr>
          <tr>
            <td style={{ width: '31.25%' }}>Coordinate provider</td>
            <td>{stop.location?.position?.provider ?? '-'}</td>
          </tr>
          <tr>
            <td style={{ width: '31.25%' }}>Platform geocoding type</td>
            <td>{getFormattedGeocodingType(stop.location)}</td>
          </tr>
          <tr>
            <td>Coordinate match level</td>
            <td>{getFormattedMatchLevel(stop.location)}</td>
          </tr>
          <tr>
            <td>Geocoding direction</td>
            <td>{getFormattedGeocodingDirection(stop.location?.position?.geocodingDirection)}</td>
          </tr>
          <tr>
            <td>Location Reference ID</td>
            <td>{stop.location?.location_reference_id ?? '-'}</td>
          </tr>
          <tr>
            <td>Booking location name</td>
            <td>{stop.location?.bookingLocationName ?? '-'}</td>
          </tr>
          <tr>
            <td>Gate</td>
            <td>
              {stop.location?.gate ?? '-'} (ID: {stop.location?.gate_id ?? '-'})
            </td>
          </tr>
          <tr>
            <td style={{ width: '18.75%' }} rowSpan={2}>
              Place
            </td>
            <td>
              {notNil(stop.location?.place) ? (
                <a
                  className="link"
                  rel="noreferrer"
                  target="_blank"
                  title="Place details"
                  href={placeViewUrl(tourCreatedBy, stop.location?.place?.place_id)}>
                  {stop.location?.place?.place_id} {stop.location?.place?.name ? `(${stop.location?.place?.name})` : ''}{' '}
                  {stop.location?.place?.isSixfoldGenerated ? '(Sixfold generated)' : ''}
                </a>
              ) : (
                '-'
              )}
            </td>
          </tr>
          <tr>
            <td>
              {notNil(stop.location?.place) ? (
                notNil(stop.place_reference_id) ? (
                  <>Place matched by reference_id: {stop.place_reference_id}</>
                ) : (
                  <>Place matched by customer-provided address</>
                )
              ) : (
                '-'
              )}
            </td>
          </tr>
          <tr>
            <td style={{ width: '18.75%' }}>Short area code</td>
            <td colSpan={2}>{stop.location?.address?.shortAreaCode ?? '-'}</td>
          </tr>
        </React.Fragment>
      )}
      <tr>
        <td colSpan={3} style={{ textAlign: 'center' }}>
          <Button size="small" onClick={() => setShowAddLocInfo(!showAddLocInfo)}>
            {showAddLocInfo ? 'Hide additional location info' : 'Show additional location info'}
          </Button>
        </td>
      </tr>
    </React.Fragment>
  );
};

const TourVehicleAllocations: React.FunctionComponent<{
  allocations: TourVehicleAllocation[];
  effectiveIds: Set<string | null | undefined> | null;
  activeAllocation: TourVehicleAllocation;
}> = ({ allocations, effectiveIds, activeAllocation }) => (
  <>
    <h3>Vehicle allocation history</h3>
    <table className="ui-table compact-table">
      <thead>
        <tr>
          <th>Source</th>
          <th>Allocation</th>
          <th>Switch Type</th>
          <th>Start Tracking at</th>
          <th>Allocated at</th>
          <th>Allocated by</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        {allocations.length === 0 ? (
          <tr style={{ textAlign: 'center' }}>
            <td colSpan={6}>No vehicle allocations</td>
          </tr>
        ) : (
          allocations.map((allocation, idx) => {
            const isEffective =
              effectiveIds === null || allocation.allocationId == null || effectiveIds.has(allocation.allocationId);
            const isActiveAllocation =
              activeAllocation != null &&
              allocation.allocationId != null &&
              allocation.allocationId === activeAllocation.allocationId;
            const isAllocationClear = allocation.clearAllocation === true;
            const allocationSource = breakWordAtChar(allocation.source, '_');
            const remoteAllocationSource = breakWordAtChar(allocation.remoteAllocationSource, '_');

            return (
              <tr
                key={idx}
                style={!isEffective ? { color: 'var(--color-neutral-text-subtle)', fontStyle: 'italic' } : undefined}
                className={classnames({
                  warning: isAllocationClear,
                  active__allocation: isActiveAllocation,
                })}>
                <td>{allocationSource}</td>
                <td>
                  <AllocationDetails allocation={allocation} />
                </td>
                <td>{allocation.allocationSwitchType ?? '-'}</td>
                <td>
                  <FormattedDate date={allocation.startTrackingAt} />
                </td>
                <td>
                  <FormattedDate date={allocation.allocatedAt} />
                </td>
                <td>
                  {notNil(allocation.allocatedByCompany) ? (
                    <Link
                      style={{ wordBreak: 'break-word' }}
                      to={Routes.Company.generatePath({ company_id: allocation.allocatedByCompany.company_id })}>
                      {allocation.allocatedByCompany.company_name !== null
                        ? allocation.allocatedByCompany.company_name
                        : 'No company name'}
                    </Link>
                  ) : (
                    '-'
                  )}
                </td>
                <td>
                  <Popover
                    content={
                      <>
                        <div style={{ whiteSpace: 'nowrap' }}>
                          <strong>Platform Source:</strong> {remoteAllocationSource}
                        </div>
                        <div>
                          <strong>Device Type:</strong> {allocation.allocationDeviceType ?? '-'}
                        </div>
                        <div
                          style={
                            allocation.disableVehicleMatching === true
                              ? { color: 'var(--color-danger-text-accent, #b33a3a)' }
                              : undefined
                          }>
                          <strong>Vehicle Matching Disabled:</strong>{' '}
                          {isNil(allocation.disableVehicleMatching)
                            ? '-'
                            : allocation.disableVehicleMatching.toString()}
                        </div>
                        {allocation.disableVehicleMatching === true && (
                          <div
                            style={{
                              color: 'var(--color-danger-text-accent, #b33a3a)',
                              marginBottom: '0.5em',
                              fontStyle: 'italic',
                            }}>
                            This means only the license plate information is shown in the UI, but the vehicle is not
                            being tracked.
                          </div>
                        )}
                        <div>
                          <strong>Auto Allocation:</strong> {allocation.isAutoAllocation?.toString() ?? '-'}
                        </div>
                        <div>
                          <strong>Outside Transport Pool:</strong>{' '}
                          {allocation.isVehicleOutsideOfTransportPool?.toString() ?? '-'}
                        </div>
                        <div>
                          <strong>Limited Visibility Allocation:</strong>{' '}
                          {allocation.isLimitedVisibilityAllocation?.toString() ?? '-'}
                        </div>
                      </>
                    }>
                    <span
                      style={
                        allocation.disableVehicleMatching === true
                          ? { color: 'var(--color-danger-text-accent, #b33a3a)' }
                          : undefined
                      }>
                      <Icon name="small-info" kind={allocation.disableVehicleMatching === true ? undefined : 'info'} />
                    </span>
                  </Popover>
                </td>
              </tr>
            );
          })
        )}
      </tbody>
    </table>
  </>
);

const TourAlerts: React.FunctionComponent<{
  tour: Tour;
}> = ({ tour }) => {
  const transportAlerts = tour.transportAlerts;

  if (transportAlerts.length === 0 || (!isAirTour(tour.visibilityServices) && !isOceanTour(tour.visibilityServices))) {
    return null;
  }

  return (
    <>
      <h3>Alerts</h3>
      <table className="ui-table compact-table">
        <thead>
          <tr>
            <th></th>
            <th>Alert</th>
            <th>Created at</th>
          </tr>
        </thead>
        <tbody>
          {transportAlerts.map((transportAlert, index) => (
            <tr key={index}>
              <td>
                <TourAlertSeverity severity={transportAlert.severity} />
              </td>
              <td>
                <Box paddingBottom="small">{transportAlert.description}</Box>
                <Pill>{transportAlert.type}</Pill>
              </td>
              <td>{<FormattedDate date={transportAlert.createdAt} />}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
};

const TourAlertSeverity: React.FunctionComponent<{
  severity: TransportAlertSeverity;
}> = ({ severity }) => {
  switch (severity) {
    case TransportAlertSeverity.INFO:
      return <AlertIcon title="Info" kind="info" name="small-info" />;

    case TransportAlertSeverity.WARNING:
      return <AlertIcon title="Warning" kind="warning" name="small-alert" />;

    case TransportAlertSeverity.BLOCKER:
      return <AlertIcon title="Blocker" kind="danger" name="small-alert" />;

    case TransportAlertSeverity.CRITICAL:
      return <AlertIcon title="Critical" kind="danger" name="small-alert" />;
  }

  exhaustiveCheck(severity);
};

type IconKind = IconProps['kind'];
type IconName = IconProps['name'];

const AlertIcon: React.FunctionComponent<{ title: string; kind: IconKind; name: IconName }> = ({
  title,
  kind,
  name,
}) => {
  return (
    <Box as="span">
      <Tooltip content={<>{title}</>}>
        <Icon kind={kind} name={name} />
      </Tooltip>
    </Box>
  );
};

const TourForwardings: React.FunctionComponent<{
  tour: Pick<Tour, 'forwardingChain'>;
  allocationsById: Map<string | null | undefined, TourVehicleAllocation>;
}> = ({ tour: { forwardingChain }, allocationsById }) => (
  <>
    <h3>Tour forwarding</h3>
    <table className="ui-table compact-table">
      <thead>
        <tr>
          <th>From</th>
          <th>To</th>
          <th>Forwarded at</th>
          <th>Forwarded by</th>
          <th>Triggered by</th>
        </tr>
      </thead>
      <tbody>
        {forwardingChain.length === 0 ? (
          <tr style={{ textAlign: 'center' }}>
            <td colSpan={4}>No forwardings</td>
          </tr>
        ) : (
          forwardingChain.map((forwarding, idx) => (
            <tr key={idx}>
              <td>
                {forwarding.from !== null ? (
                  <Link to={Routes.Company.generatePath({ company_id: forwarding.from.company_id })}>
                    {forwarding.from.company_name}
                  </Link>
                ) : (
                  'Unspecified'
                )}
              </td>
              <td>
                <Link to={Routes.Company.generatePath({ company_id: forwarding.to.company_id })}>
                  {forwarding.to.company_name}
                </Link>
              </td>
              <td>
                <FormattedDate date={forwarding.forwardedAt} />
              </td>
              <td>
                {forwarding.forwardedBy !== null ? (
                  <Link to={Routes.User.generatePath({ user_id: forwarding.forwardedBy.user_id })}>
                    {forwarding.forwardedBy.first_name} {forwarding.forwardedBy.last_name}
                  </Link>
                ) : (
                  '-'
                )}
              </td>
              <td>
                {(() => {
                  if (forwarding.triggeredByAllocationId === null) return '-';
                  const allocation = allocationsById.get(forwarding.triggeredByAllocationId);
                  if (allocation != null) return <AllocationDetails allocation={allocation} />;
                  return forwarding.triggeredByAllocationId;
                })()}
              </td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  </>
);

const VisibilityRanges: React.FunctionComponent<{
  tour: Pick<Tour, 'visibilitySegments' | 'vehicleAllocationHistory'>;
}> = ({ tour: { visibilitySegments, vehicleAllocationHistory } }) => (
  <>
    <h3>Visibility ranges</h3>
    <table className="ui-table compact-table">
      <thead>
        <tr>
          <th>Vehicle</th>
          <th>From</th>
          <th>To</th>
        </tr>
      </thead>
      <tbody>
        {visibilitySegments.length === 0 ? (
          <tr style={{ textAlign: 'center' }}>
            <td colSpan={3}>No visibility ranges</td>
          </tr>
        ) : (
          visibilitySegments.map((segment, idx) => {
            const allocation = vehicleAllocationHistory.find(({ vehicleId }) => vehicleId === segment.vehicleId);

            return (
              <tr key={idx}>
                <td>
                  {segment.vehicleId !== null ? (
                    <Link to={Routes.Vehicle.generatePath({ vehicle_id: segment.vehicleId })}>
                      {allocation?.vehicleLicensePlateNumber ?? <i>#{segment.vehicleId}</i>}
                    </Link>
                  ) : (
                    <i>#{segment.vehicleId}</i>
                  )}
                </td>
                <td>
                  <FormattedDate date={segment.visibilityStartDate} />
                </td>
                <td>
                  <FormattedDate date={segment.visibilityEndDate} />
                </td>
              </tr>
            );
          })
        )}
      </tbody>
    </table>
  </>
);

const TrackingRanges: React.FunctionComponent<{
  tour: Pick<Tour, 'trackingSegments' | 'vehicleAllocationHistory'>;
}> = ({ tour: { trackingSegments, vehicleAllocationHistory } }) => (
  <>
    <h3>Tracking ranges</h3>
    <table className="ui-table compact-table">
      <thead>
        <tr>
          <th>Vehicle</th>
          <th>From</th>
          <th>To</th>
        </tr>
      </thead>
      <tbody>
        {trackingSegments.length === 0 ? (
          <tr style={{ textAlign: 'center' }}>
            <td colSpan={3}>No tracking ranges</td>
          </tr>
        ) : (
          trackingSegments.map((segment, idx) => {
            const allocation = vehicleAllocationHistory.find(({ vehicleId }) => vehicleId === segment.vehicleId);

            return (
              <tr key={idx}>
                <td>
                  {segment.vehicleId !== null ? (
                    <Link to={Routes.Vehicle.generatePath({ vehicle_id: segment.vehicleId })}>
                      {allocation?.vehicleLicensePlateNumber ?? <i>#{segment.vehicleId}</i>}
                    </Link>
                  ) : (
                    <i>#{segment.vehicleId}</i>
                  )}
                </td>
                <td>
                  <FormattedDate date={segment.trackingStartDate} />
                </td>
                <td>
                  <FormattedDate date={segment.trackingEndDate} />
                </td>
              </tr>
            );
          })
        )}
      </tbody>
    </table>
  </>
);

const CalculatedEmissions: React.FunctionComponent<{
  emissions: TourCO2Emissions | null;
  type: string;
}> = ({ type, emissions }) => {
  const [allCollapsed, setAllCollapsed] = useState(false);
  const [collapsedLegIndexes, setCollapsedLegIndexes] = useState(new Set());
  const emissionsText = type === 'planned' ? 'Planned emissions' : 'Telemetry emissions';

  const toggleLegCollapse = useCallback(
    (legIndex: number) => {
      const newSet = new Set(collapsedLegIndexes);

      if (collapsedLegIndexes.has(legIndex)) {
        newSet.delete(legIndex);
        setAllCollapsed(false);
      } else {
        newSet.add(legIndex);
        if (newSet.size === emissions?.legs.length) {
          setAllCollapsed(true);
        }
      }

      setCollapsedLegIndexes(newSet);
    },
    [collapsedLegIndexes, emissions?.legs.length],
  );

  const toggleAllCollapsing = useCallback(() => {
    if (allCollapsed) {
      setCollapsedLegIndexes(new Set());
    } else {
      setCollapsedLegIndexes(new Set(emissions?.legs.map((_, i) => i)));
    }
    setAllCollapsed(!allCollapsed);
  }, [allCollapsed, emissions?.legs]);

  return (
    <>
      <table className="ui-table compact-table">
        <thead>
          <tr>
            <th colSpan={3}>{emissionsText}</th>
          </tr>
        </thead>
        <tbody>
          <tr style={{ verticalAlign: 'top' }}>
            <td style={{ width: '25%' }} rowSpan={3}>
              <b>CO2 total emissions</b>
            </td>
            <td>Tank-to-wheel tonnes</td>
            <td>{emissions?.co2TankToWheelTonnes ?? '-'}</td>
          </tr>
          <tr>
            <td>Well-to-tank tonnes</td>
            <td>{emissions?.co2WellToTankTonnes ?? '-'}</td>
          </tr>
          <tr>
            <td>Well-to-wheel tonnes</td>
            <td>{emissions?.co2WellToWheelTonnes ?? '-'}</td>
          </tr>
          <tr style={{ verticalAlign: 'top' }}>
            <td rowSpan={6}>
              <b>Cargo</b>
            </td>
            <td>Container empty weight</td>
            <td>{emissions?.cargo.containerEmptyWeight ?? '-'}</td>
          </tr>
          <tr>
            <td>Tons per teu</td>
            <td>{emissions?.cargo.tonsPerTeu ?? '-'}</td>
          </tr>
          <tr>
            <td>Tons per feu</td>
            <td>{emissions?.cargo.tonsPerFeu ?? '-'}</td>
          </tr>
          <tr>
            <td>Weight</td>
            <td>{emissions?.cargo.weight ?? '-'}</td>
          </tr>
          <tr>
            <td>Volume weight</td>
            <td>{emissions?.cargo.volumeWeight ?? '-'}</td>
          </tr>
          <tr>
            <td>Unit</td>
            <td>{emissions?.cargo.unit ?? '-'}</td>
          </tr>
        </tbody>
      </table>

      <div>
        {emissions?.legs.map((leg, legIdx) => (
          <React.Fragment key={legIdx}>
            {legIdx === 0 && (
              <h3>
                {`${emissionsText} legs (${emissions?.legs.length})`}
                <Button
                  kind="ghost"
                  size="xsmall"
                  iconStart={allCollapsed ? 'small-chevron-up' : 'small-chevron-down'}
                  hideLabel
                  onClick={toggleAllCollapsing}
                  style={{ float: 'right' }}>
                  Toggle all legs
                </Button>
              </h3>
            )}

            <div className="item tour__tour-details__stop-item">
              <table className="ui-table compact-table">
                <thead>
                  <tr>
                    <th colSpan={3}>
                      <div className="tour__tour-details__stop-item__stop-header">
                        <h3 className="tour__tour-details__stop-item__stop-header__address">
                          {`${emissionsText} leg #${legIdx + 1}`}
                        </h3>
                        <Button
                          kind="ghost"
                          size="xsmall"
                          iconStart={collapsedLegIndexes.has(legIdx) ? 'small-chevron-up' : 'small-chevron-down'}
                          hideLabel
                          onClick={() => toggleLegCollapse(legIdx)}>
                          Toggle leg
                        </Button>
                      </div>
                    </th>
                  </tr>
                </thead>

                {!collapsedLegIndexes.has(legIdx) && (
                  <tbody>
                    <tr style={{ verticalAlign: 'top' }}>
                      <td>
                        <b>Transport mode</b>
                      </td>
                      <td>{leg.transportMode}</td>
                    </tr>
                    <tr>
                      <td>
                        <b>Type</b>
                      </td>
                      <td>{leg.type}</td>
                    </tr>
                    <tr>
                      <td>
                        <b>Distance in KM</b>
                      </td>
                      <td>{leg.distanceKM ?? '-'}</td>
                    </tr>

                    <tr style={{ verticalAlign: 'top' }}>
                      <td rowSpan={2}>
                        <b>Start location</b>
                      </td>
                      <td>Latitude</td>
                      <td>{leg.startLocation.lat}</td>
                    </tr>
                    <tr>
                      <td>Longitude</td>
                      <td>{leg.startLocation.lng}</td>
                    </tr>

                    <tr style={{ verticalAlign: 'top' }}>
                      <td rowSpan={2}>
                        <b>End location</b>
                      </td>
                      <td>Latitude</td>
                      <td>{leg.endLocation.lat}</td>
                    </tr>
                    <tr>
                      <td>Longitude</td>
                      <td>{leg.endLocation.lng}</td>
                    </tr>

                    <tr style={{ verticalAlign: 'top' }}>
                      <td rowSpan={7 * leg.emissions.length + 1}>
                        <b>Emissions</b>
                      </td>
                    </tr>
                    {leg.emissions.map((emission, emissionIdx) => (
                      <React.Fragment key={emissionIdx}>
                        <tr>
                          <td>
                            <b>Emission #{emissionIdx + 1}</b>
                          </td>
                        </tr>
                        <tr>
                          <td>Mode</td>
                          <td>{emission.emissionMode}</td>
                        </tr>
                        <tr>
                          <td>Type</td>
                          <td>{emission.type}</td>
                        </tr>
                        <tr>
                          <td>Unit</td>
                          <td>{emission.unit}</td>
                        </tr>
                        <tr>
                          <td>Tank to wheel</td>
                          <td>{emission.tankToWheel}</td>
                        </tr>
                        <tr>
                          <td>Well to tank</td>
                          <td>{emission.wellToTank}</td>
                        </tr>
                        <tr>
                          <td>Total</td>
                          <td>{emission.total}</td>
                        </tr>
                      </React.Fragment>
                    ))}

                    <tr style={{ verticalAlign: 'top' }}>
                      <td rowSpan={2}>
                        <b>External costs</b>
                      </td>
                      <td>Currency</td>
                      <td>{leg.externalCosts?.currency}</td>
                    </tr>
                    <tr>
                      <td>Total external costs</td>
                      <td>{leg.externalCosts?.totalExternalCosts}</td>
                    </tr>
                    {leg.externalCosts?.noiseCosts?.map((noiseCost, noiseCostIdx) => (
                      <React.Fragment key={noiseCostIdx}>
                        {noiseCostIdx === 0 && (
                          <tr style={{ verticalAlign: 'top' }}>
                            <td rowSpan={3 * (leg.externalCosts?.noiseCosts?.length ?? 0) + 1}>
                              <b>External costs - noise costs</b>
                            </td>
                          </tr>
                        )}
                        <tr>
                          <td>
                            <b>Noise cost #{noiseCostIdx + 1}</b>
                          </td>
                        </tr>
                        <tr>
                          <td>Transport mode</td>
                          <td>{noiseCost.transportMode}</td>
                        </tr>
                        <tr>
                          <td>Noise cost value</td>
                          <td>{noiseCost.noiseCostValue}</td>
                        </tr>
                      </React.Fragment>
                    ))}

                    {leg.externalCosts?.emissionCosts?.map((emissionCost, emissionCostIdx) => (
                      <React.Fragment key={emissionCostIdx}>
                        {emissionCostIdx === 0 && (
                          <tr style={{ verticalAlign: 'top' }}>
                            <td rowSpan={5 * (leg.externalCosts?.emissionCosts?.length ?? 0) + 1}>
                              <b>External costs - emission costs</b>
                            </td>
                          </tr>
                        )}
                        <tr>
                          <td>
                            <b>Emission cost #{emissionCostIdx + 1}</b>
                          </td>
                        </tr>
                        <tr>
                          <td>Name</td>
                          <td>{emissionCost.name}</td>
                        </tr>
                        <tr>
                          <td>Tank to wheel</td>
                          <td>{emissionCost.tankToWheel}</td>
                        </tr>
                        <tr>
                          <td>Well to tank</td>
                          <td>{emissionCost.wellToTank}</td>
                        </tr>
                        <tr>
                          <td>Total</td>
                          <td>{emissionCost.total}</td>
                        </tr>
                      </React.Fragment>
                    ))}

                    {leg.externalCosts?.accidentCosts.map((accidentCost, accidentCostIdx) => (
                      <React.Fragment key={accidentCostIdx}>
                        {accidentCostIdx === 0 && (
                          <tr style={{ verticalAlign: 'top' }}>
                            <td rowSpan={3 * (leg.externalCosts?.accidentCosts?.length ?? 0) + 1}>
                              <b>External costs - accident costs</b>
                            </td>
                          </tr>
                        )}
                        <tr>
                          <td>
                            <b>Accident cost #{accidentCostIdx + 1}</b>
                          </td>
                        </tr>
                        <tr>
                          <td>Transport mode</td>
                          <td>{accidentCost.transportMode}</td>
                        </tr>
                        <tr>
                          <td>Accident cost value</td>
                          <td>{accidentCost.accidentCostValue}</td>
                        </tr>
                      </React.Fragment>
                    ))}

                    <tr style={{ verticalAlign: 'top' }}>
                      <td rowSpan={1}>
                        <b>Carriage parameters</b>
                      </td>
                      <td>Biofuel share</td>
                      <td>{leg.carriageParameters.bioFuelShare}</td>
                    </tr>

                    {leg.carriageParameters.carriageModeParameters?.map((carriageModeParameter, carriageMPIdx) => (
                      <React.Fragment key={carriageMPIdx}>
                        {carriageMPIdx === 0 && (
                          <tr style={{ verticalAlign: 'top' }}>
                            <td rowSpan={12 * leg.carriageParameters.carriageModeParameters.length + 1}>
                              <b>Carriage mode parameters</b>
                            </td>
                          </tr>
                        )}
                        <tr>
                          <td>
                            <b>Carriage mode parameter #{carriageMPIdx + 1}</b>
                          </td>
                        </tr>
                        <tr>
                          <td>Mode</td>
                          <td>{carriageModeParameter.mode}</td>
                        </tr>
                        <tr>
                          <td>Emission class</td>
                          <td>{carriageModeParameter.emissionClass ?? '-'}</td>
                        </tr>
                        <tr>
                          <td>Empty-run factor</td>
                          <td>{carriageModeParameter.emptyRunFactor ?? '-'}</td>
                        </tr>
                        <tr>
                          <td>Ferry routing</td>
                          <td>{carriageModeParameter.ferryRouting ?? '-'}</td>
                        </tr>
                        <tr>
                          <td>Fuel type</td>
                          <td>{carriageModeParameter.fuelType ?? '-'}</td>
                        </tr>
                        <tr>
                          <td>Load factor</td>
                          <td>{carriageModeParameter.loadFactor ?? '-'}</td>
                        </tr>
                        <tr>
                          <td>Lorry class</td>
                          <td>{carriageModeParameter.lorryClass ?? '-'}</td>
                        </tr>
                        <tr>
                          <td>Drive class</td>
                          <td>{carriageModeParameter.driveClass ?? '-'}</td>
                        </tr>
                        <tr>
                          <td>Aircraft type</td>
                          <td>{carriageModeParameter.aircraftType ?? '-'}</td>
                        </tr>
                        <tr>
                          <td>Seaship type</td>
                          <td>{carriageModeParameter.seaShipType ?? '-'}</td>
                        </tr>
                        <tr>
                          <td>Inland ship type</td>
                          <td>{carriageModeParameter.inlandShipType ?? '-'}</td>
                        </tr>
                      </React.Fragment>
                    ))}
                  </tbody>
                )}
              </table>
            </div>
          </React.Fragment>
        ))}
      </div>
    </>
  );
};

const FuelBasedEmissions: React.FunctionComponent<{ emissions: TourFuelBasedEmissions | null }> = ({ emissions }) => (
  <>
    <table className="ui-table compact-table">
      <thead>
        <tr>
          <th colSpan={3}>Fuel-based emissions</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>Accumulated fuel consumption liters</td>
          <td>{emissions?.accumulatedFuelConsumptionLiters ?? '-'}</td>
        </tr>
        <tr>
          <td>Fuel type</td>
          <td>{emissions?.fuelType ?? '-'}</td>
        </tr>
        <tr>
          <td>Well-to-tank tonnes</td>
          <td>{emissions?.co2WellToTankTonnes ?? '-'}</td>
        </tr>
        <tr>
          <td>Tank-to-wheel tonnes</td>
          <td>{emissions?.co2TankToWheelTonnes ?? '-'}</td>
        </tr>
        <tr>
          <td>Well-to-wheel tonnes</td>
          <td>{emissions?.co2WellToWheelTonnes ?? '-'}</td>
        </tr>
      </tbody>
    </table>
  </>
);

const DrivenDistance: React.FunctionComponent<{ data: TourDrivenDistance | null }> = ({ data }) => (
  <>
    <table className="ui-table compact-table">
      <thead>
        <tr>
          <th colSpan={3}>Driven distance</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>Distance in kilometers</td>
          <td>{data?.distanceInKilometers ?? '-'}</td>
        </tr>
        <tr>
          <td>Source</td>
          <td>{data?.source ?? '-'}</td>
        </tr>
      </tbody>
    </table>
  </>
);

const EmissionsCalculationErrors: React.FunctionComponent<{
  calculationErrors: EmissionsCalculationError[] | null;
}> = ({ calculationErrors }) => (
  <>
    <table className="ui-table compact-table">
      <thead>
        <tr>
          <th colSpan={3}>Emissions calculation errors</th>
        </tr>
      </thead>
      <tbody>
        {calculationErrors?.map((error, errorIdx) => (
          <React.Fragment key={errorIdx}>
            <tr style={{ verticalAlign: 'top' }}>
              <td rowSpan={2}>
                <b>Error #{errorIdx + 1}</b>
              </td>
              <td>Type</td>
              <td>{error.type ?? '-'}</td>
            </tr>
            <tr>
              <td>Code</td>
              <td>{error.code ?? '-'}</td>
            </tr>
          </React.Fragment>
        ))}
        {calculationErrors?.length === 0 && (
          <tr>
            <td>No errors</td>
          </tr>
        )}
      </tbody>
    </table>
  </>
);

const CustomFieldsTableItems: React.FC<{
  customFields: CustomField[];
}> = ({ customFields }) => {
  return (
    <>
      {customFields.map((customField, idx) => (
        <tr key={`${customField.fieldName}_${idx}`}>
          <td>{customField.fieldName}</td>
          <td>{customField.values.map(({ value }) => value).join(', ')}</td>
          <td>{customField.visibility ?? '-'}</td>
        </tr>
      ))}
    </>
  );
};

const DeliveriesCustomFieldsTable: React.FunctionComponent<{
  customFields: CustomField[];
}> = ({ customFields }) => {
  return (
    <>
      <table className="ui-table compact-table" style={{ border: 'none' }}>
        <tbody>
          <tr style={{ verticalAlign: 'top' }}>
            <td>Name</td>
            <td>Value(s)</td>
            <td>Visibility</td>
          </tr>
          <CustomFieldsTableItems customFields={customFields} />
        </tbody>
      </table>
    </>
  );
};

const Deliveries: React.FunctionComponent<{ tour: Pick<Tour, 'deliveries'> }> = ({ tour: { deliveries } }) => {
  return (
    <>
      <h3>Deliveries</h3>
      {deliveries.length === 0 ? (
        <table className="ui-table compact-table">
          <tbody>
            <tr style={{ textAlign: 'center' }}>
              <td>No deliveries</td>
            </tr>
          </tbody>
        </table>
      ) : (
        deliveries.map((delivery) => (
          <>
            <table className="ui-table compact-table" key={delivery.deliveryId}>
              <thead>
                <tr>
                  <th colSpan={2}>
                    {delivery.deliveryNumber} (ID: {delivery.deliveryId})
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={{ width: '31.25%' }}>
                    <b>Order number</b>
                  </td>
                  <td>{delivery.orderNumber ?? '-'}</td>
                </tr>
                <tr>
                  <td>
                    <b>Purchase order number</b>
                  </td>
                  <td>{delivery.purchaseOrderNumber ?? '-'}</td>
                </tr>
                <tr>
                  <td>
                    <b>Sequence number</b>
                  </td>
                  <td>{delivery.sequenceNumber ?? '-'}</td>
                </tr>

                <tr>
                  <td>
                    <b>Delivery tour Id</b>
                  </td>
                  <td>
                    {delivery.deliveryTourId !== null ? (
                      <Link to={Routes.Tour.generatePath({ tour_id: delivery.deliveryTourId })}>
                        {delivery.deliveryTourId}
                      </Link>
                    ) : (
                      '-'
                    )}
                  </td>
                </tr>
                <tr>
                  <td>
                    <b>Main mode</b>
                  </td>
                  <td>{delivery.mainMode ?? '-'}</td>
                </tr>
                <tr>
                  <td>
                    <b>Storage positions</b>
                  </td>
                  <td>{delivery.storagePositions ?? '-'}</td>
                </tr>
                <tr>
                  <td>
                    <b>Transport unit</b>
                  </td>
                  <td>
                    {delivery.transportUnit?.value ?? '-'} {delivery.transportUnit?.unit}
                  </td>
                </tr>
                <tr>
                  <td>
                    <b>Volume</b>
                  </td>
                  <td>
                    {delivery.volume?.value ?? '-'} {delivery.volume?.unit}
                  </td>
                </tr>
                <tr>
                  <td>
                    <b>Loading length</b>
                  </td>
                  <td>
                    {delivery.loadingLength?.value ?? '-'} {delivery.loadingLength?.unit}
                  </td>
                </tr>
                <tr>
                  <td>
                    <b>Incoterms</b>
                  </td>
                  <td>
                    {delivery.incoterms?.city ?? '-'} {delivery.incoterms?.term}
                  </td>
                </tr>
                <DeliveryContactsRow
                  shipperContacts={delivery.shipperContacts}
                  consigneeContacts={delivery.consigneeContacts}
                  customerContacts={delivery.customerContacts}
                />
                {delivery.customFields.length > 0 && (
                  <tr>
                    <td>
                      <b>Custom fields</b>
                    </td>
                    <td>
                      <DeliveriesCustomFieldsTable customFields={delivery.customFields} />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </>
        ))
      )}
    </>
  );
};
function visibilityIndexStyle(index: Tour['visibilityIndex']): React.CSSProperties {
  if (index === null) {
    return {};
  }

  const hue = (index * 120).toString(10);

  return {
    textDecoration: `underline 2px solid hsl(${hue},60%,50%)`,
  };
}

/**
 *
 * Utility components
 *
 */

const VehicleInfo: React.FC<{
  vehicle: { vehicleId: string | null; vehicleLicensePlateNumber: string } | null;
}> = ({ vehicle }) => {
  const vehicleId = vehicle?.vehicleId ?? null;
  const vehicleLicensePlateNumber = vehicle?.vehicleLicensePlateNumber ?? null;

  return vehicleId !== null && vehicleLicensePlateNumber !== null ? (
    <Link to={Routes.Vehicle.generatePath({ vehicle_id: vehicleId })}>{vehicleLicensePlateNumber}</Link>
  ) : vehicleId !== null ? (
    <Link to={Routes.Vehicle.generatePath({ vehicle_id: vehicleId })}>
      <i>ID #{vehicleId}</i>
    </Link>
  ) : vehicleLicensePlateNumber !== null ? (
    <>{vehicleLicensePlateNumber} (No vehicle ID)</>
  ) : (
    <>-</>
  );
};

const StopETADetails: React.FunctionComponent<{ stop: TourStop; tourId: number }> = ({ stop, tourId }) => {
  const hasAltEtas = stop.etas !== null && stop.etas.length > 0;
  const [showAltEtas, setShowAltEtas] = useState<boolean>(false);

  return (
    <React.Fragment>
      <table className="ui-table compact-table">
        <ETADetails stop={stop} title="ETA" tourId={tourId} />
        {showAltEtas
          ? (stop.etas ?? []).map((eta, i) => {
              return <ETADetails stop={eta} title={`Additional ETA #${i + 1}`} key={i} tourId={tourId} />;
            })
          : null}
        {hasAltEtas && (
          <tfoot>
            <tr>
              <td colSpan={3} style={{ textAlign: 'center' }}>
                <Button size="xsmall" onClick={() => setShowAltEtas(!showAltEtas)}>
                  {showAltEtas ? 'Hide additional ETAs' : `Show other ETAs (${stop.etas?.length ?? 0})`}
                </Button>
              </td>
            </tr>
          </tfoot>
        )}
      </table>
    </React.Fragment>
  );
};

const ETADetails: React.FunctionComponent<{
  stop: TourStopEta;
  title: React.ReactNode;
  tourId: number;
}> = ({ stop, title, tourId }) => {
  const { etaSource, etaType } = getEtaSourceAndType(stop);
  return (
    <tbody>
      <tr style={{ verticalAlign: 'top' }}>
        <td
          style={{ width: '18.75%' }}
          rowSpan={getRowSpan(
            stop,
            8,
            null,
            'estimated_arrival_unregulated',
            'estimated_arrival_regulated',
            'estimated_arrival_accuracy',
            'estimated_arrival_distance_in_meters',
            'estimated_arrival',
            'estimated_arrival_status',
          )}>
          <b>{title}</b>
        </td>
        <td style={{ width: '31.25%' }}>ETA source(TYPE)</td>
        <td>
          {etaSource} {etaType !== undefined ? `(${etaType})` : null}
        </td>
      </tr>
      <tr>
        <td>ETA history</td>
        <td>
          <a
            className="link"
            rel="noopener noreferrer"
            target="_blank"
            title="Stop ETA report"
            href={tourStopEtaHistoryUrl({
              tourId,
              stopId: stop.stop_id,
            })}>
            Data studio ETA report
          </a>
        </td>
      </tr>
      {stop.estimated_arrival != null && (
        <tr>
          <td>ETA</td>
          <td>
            <FormattedDate date={stop.estimated_arrival} />
          </td>
        </tr>
      )}
      {stop.estimated_arrival_unregulated !== null && (
        <tr>
          <td>ETA (without breaks)</td>
          <td>
            <FormattedDate date={stop.estimated_arrival_unregulated} />
          </td>
        </tr>
      )}
      {stop.estimated_arrival_regulated !== null && (
        <tr>
          <td>ETA (with breaks)</td>
          <td>
            <FormattedDate date={stop.estimated_arrival_regulated} />
          </td>
        </tr>
      )}
      {stop.estimated_arrival_status != null && (
        <tr>
          <td>Status</td>
          <td>{stop.estimated_arrival_status}</td>
        </tr>
      )}
      {stop.estimated_arrival_accuracy != null && (
        <tr>
          <td>Accuracy</td>
          <td>{stop.estimated_arrival_accuracy}</td>
        </tr>
      )}
      {stop.estimated_arrival_distance_in_meters !== null && (
        <tr>
          <td>Remaining distance</td>
          <td>
            {
              <>
                {(stop.estimated_arrival_distance_in_meters / 1000).toFixed(0)}
                km ({stop.estimated_arrival_distance_in_meters}
                m)
              </>
            }
          </td>
        </tr>
      )}
    </tbody>
  );
};

function getEtaSourceAndType(stop: TourStopEta): {
  etaSource: EtaType | undefined;
  etaType: EtaType | ExternalEtaSource | undefined;
} {
  if (stop.etaType === EtaType.ML_MODEL) {
    return { etaSource: EtaType.INTERNAL, etaType: stop.etaType };
  }
  if (stop.externalEtaSource !== null) {
    return { etaSource: EtaType.EXTERNAL, etaType: stop.externalEtaSource };
  }
  return { etaSource: stop.etaType ?? undefined, etaType: undefined };
}

const AllocationDetails: React.FunctionComponent<{ allocation: TourVehicleAllocation }> = ({ allocation }) => {
  // NOTE: STOP_ALLOCATION is deprecated and no longer supported for new allocations.
  // This code is kept for displaying historical stop allocation data only.
  if (allocation.allocationType === TourVehicleAllocationType.STOP_ALLOCATION) {
    return <React.Fragment>Stop allocation is deprecated.</React.Fragment>;
  }

  const licensePlateOrTrackerId = allocation.vehicleLicensePlateNumber ?? allocation.remoteTrackerId;

  if (allocation.source === TourVehicleAllocationSource.TOUR_BASED_STREAM) {
    return (
      <>
        {allocation.vehicleId}
        {licensePlateOrTrackerId !== null ? ` (${licensePlateOrTrackerId})` : ''}
      </>
    );
  }

  if (allocation.vehicleLicensePlateNumber !== null && allocation.vehicleId !== null) {
    return (
      <Link to={Routes.Vehicle.generatePath({ vehicle_id: allocation.vehicleId })}>
        {allocation.vehicleLicensePlateNumber}
      </Link>
    );
  }

  if (licensePlateOrTrackerId !== null) {
    return <React.Fragment>{licensePlateOrTrackerId}</React.Fragment>;
  }

  if (allocation.clearAllocation === true) {
    return <React.Fragment>Allocation cleared</React.Fragment>;
  }

  return <React.Fragment>Unallocated</React.Fragment>;
};

const TourHeaderButtons: React.FunctionComponent<{ tour: Tour } & TourDetailsViewProps> = ({
  tour,
  onTourAbort,
  onTourRecalculate,
  onAddTourTag,
  onRemoveTourTag,
}) => {
  const isNotAdmin = !useIsAdmin();
  const hasTourEditPermission = useHasScopes([BackofficeScope.companyWriteTours]);
  const isPrivilegedUser = !isNotAdmin || hasTourEditPermission;
  const [abortState, setAbortState] = useState<boolean>(false);
  const [recalcState, setRecalcState] = useState<boolean>(false);

  const updateAbortState = (newState: boolean): void => {
    setAbortState(newState);
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    newState ? setRecalcState(false) : null;
  };
  const updateRecalcState = (newState: boolean): void => {
    setRecalcState(newState);
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    newState ? setAbortState(false) : null;
  };
  return (
    <div style={{ display: 'inline-flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
      <span style={{ margin: 0, whiteSpace: 'nowrap', fontSize: '1.4em', fontWeight: 600 }}>Tour #{tour.tour_id}</span>
      <TagsInput
        tags={tour.tags}
        availableTags={tour.availableTourTags}
        addTag={(tagName: string) =>
          onAddTourTag({
            tourId: tour.tour_id.toString(),
            tagName,
          })
        }
        removeTag={(tagName: string) => onRemoveTourTag({ tourId: tour.tour_id.toString(), tagName })}
      />
      <ConfirmButton
        size="mini"
        label="Abort"
        initialButtonColor="subtle"
        showConfirmationProp={abortState}
        updateConfirmState={updateAbortState}
        disabled={!isPrivilegedUser}
        onConfirm={() => onTourAbort(tour.tour_id)}
      />
      <ConfirmButton
        size="mini"
        label="Recalc"
        initialButtonColor="subtle"
        showConfirmationProp={recalcState}
        updateConfirmState={updateRecalcState}
        disabled={!isPrivilegedUser}
        onConfirm={() => onTourRecalculate(tour.tour_id)}
      />
    </div>
  );
};
